import { RedistributionImportInputData } from '../../../redistributionImportTypes';
import { useSelector } from 'react-redux';
import { ReduxState, useAppDispatch } from '../../../../../../../../store';
import React, { useCallback, useMemo } from 'react';
import {
  setImportContent,
  setImportItems,
} from '../../../../../../../Calculations/calculationsSlice';
import { Column } from '@ydistri/ds';
import ListItemsTextArea from '../../common/ListItemsTextArea';
import useRedistributionImportFormat from '../../../useRedistributionImportFormat';
import {
  ImportedItemRow,
  ImportedItemValidator,
} from '../../../../../../../../lib/imports/importTypes';
import { createRedistributionImportValidators } from '../../../redistributionImportValidatorsLib';
import {
  getPlainTextFromStructuredData,
  selectImportableItemParser,
} from '../../../../../../../../lib/imports/importLib';

interface RedistributionImportTextAreaProps {
  label?: string;
  maxWidth?: string;
}

const RedistributionImportTextArea: React.FC<RedistributionImportTextAreaProps> = ({
  label,
  maxWidth,
}) => {
  const dispatch = useAppDispatch();
  const importContent = useSelector((state: ReduxState) => {
    return state.calculations.redistributionImport.inputContent;
  });

  const instructionSpecification = useSelector(
    (state: ReduxState) => state.calculations.redistributionImport.instructionSpecification,
  );

  const textAreaChangeHandler = useCallback(
    (newValue: string, rows?: readonly ImportedItemRow<RedistributionImportInputData>[]) => {
      dispatch(setImportContent(newValue));

      if (rows) {
        dispatch(setImportItems(rows));
      }
    },
    [dispatch],
  );

  const inputFormat = useRedistributionImportFormat();

  const identifiers = inputFormat.map(identifier => identifier.label).join(', ');
  const itemsPlaceholder = `Paste ${identifiers} from Excel (or separated by commas)`;

  const itemSerializer = useCallback(
    (data: readonly ImportedItemRow<RedistributionImportInputData>[]) => {
      return getPlainTextFromStructuredData(data, inputFormat, ',');
    },
    [inputFormat],
  );

  const validators = useMemo((): ImportedItemValidator<RedistributionImportInputData>[] => {
    return createRedistributionImportValidators(instructionSpecification, inputFormat);
  }, [inputFormat, instructionSpecification]);

  return (
    <Column $maxWidth={maxWidth}>
      <ListItemsTextArea<RedistributionImportInputData>
        id="RedistributionImportTextArea"
        label={label}
        value={importContent}
        placeholder={itemsPlaceholder}
        parserProvider={selectImportableItemParser}
        onValueChanged={textAreaChangeHandler}
        serializer={itemSerializer}
        inputFormat={inputFormat}
        validators={validators}
      />
    </Column>
  );
};

export default RedistributionImportTextArea;
